import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingUp, CheckCircle2, ArrowRight, Clock, AlertCircle, Calculator } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Phase2Bewertung = () => {
  const bewertungsSchritte = [
    { titel: 'WAULT-Berechnung', details: 'Durchschnittliche gewichtete Restlaufzeit aller Mietverträge inkl. Optionen', pflicht: true },
    { titel: 'Marktmieten-Vergleich', details: 'Ist-Miete vs. Marktmiete für Standort und Assetklasse', pflicht: true },
    { titel: 'Vergleichswertverfahren', details: 'Analyse von 5-8 vergleichbaren Transaktionen (€/qm, Nettoanfangsrendite)', pflicht: true },
    { titel: 'Ertragswertverfahren', details: 'DCF-Modell mit 10 Jahren Cashflow-Prognose, Exit-Yield', pflicht: true },
    { titel: 'Standortanalyse', details: 'Mikro-Lage (Fußgängerfrequenz, Kaufkraft, Wettbewerb, Erreichbarkeit)', pflicht: true },
    { titel: 'Objektzustand', details: 'Bausubstanz, Technik, Modernisierungsbedarf (kostet 50-150 €/qm)', pflicht: true },
    { titel: 'Mieterqualität', details: 'Bonität Mieter (Creditreform-Score), Filialperformance, Expansionsstrategie', pflicht: true },
    { titel: 'Vertrags-Check', details: 'Indexierung? Umsatzmiete? Instandhaltung? Konkurrenzschutz?', pflicht: true },
    { titel: 'Grundbuch-Prüfung', details: 'Lasten (Grundschulden, Wegerechte, Dienstbarkeiten)', pflicht: false },
    { titel: 'Erschließung', details: 'Straße, Ver-/Entsorgung, Breitband, Anlieferung', pflicht: false }
  ];

  const verantwortlichkeiten = [
    { wer: 'Eigentümer', aufgaben: ['Realistische Preisvorstellung entwickeln', 'Optimierungspotenziale umsetzen (z.B. Mietvertrag verlängern)', 'Verkaufszeitpunkt festlegen', 'Steuerliche Planung mit Berater'] },
    { wer: 'RetailInvest', aufgaben: ['Professionelles Gutachten erstellen (nicht zertifiziert)', 'Marktdaten recherchieren (Transaktionen, Yields)', 'SWOT-Analyse des Objekts', 'Verkaufspreis-Empfehlung (Spanne: optimistisch/realistisch/konservativ)', 'Positionierung festlegen (Off-Market oder öffentlich?)'] },
    { wer: 'Gutachter (optional)', aufgaben: ['Zertifiziertes Verkehrswertgutachten nach ImmoWertV (€ 3.000-8.000)', 'Nur nötig bei Banken-Finanzierung Käufer oder Erbschaft/Scheidung'] }
  ];

  const typischeProbleme = [
    { problem: 'Eigentümer überschätzt Wert deutlich ("Nachbar hat 20% mehr bekommen")', loesung: 'Transparenz: Warum ist Nachbar-Objekt anders? (längerer Mietvertrag, besserer Mieter, modernere Immobilie). Realistische Vergleichsobjekte zeigen.' },
    { problem: 'Mietvertrag läuft in 2 Jahren aus → Käufer zahlen 15-25% weniger', loesung: 'VOR Verkauf Mietvertragsverlängerung verhandeln (z.B. +5 Jahre gegen moderate Mieterhöhung). Steigert Wert um € 200-500 pro qm Verkaufsfläche.' },
    { problem: 'Marktmiete ist 30% über Ist-Miete → Käufer kalkuliert Leerstand-Risiko ein', loesung: 'Positiv kommunizieren: "Upside-Potenzial bei Neuvermietung" + "Günstiger Einstieg für Value-Add-Investor".' },
    { problem: 'Standort gilt als "B-Lage" → Yield-Aufschlag von 1-2%', loesung: 'Stärken herausarbeiten: Alleinstellungsmerkmal? Kaufkraft-Entwicklung positiv? Exklusivität durch Konkurrenzschutz? Parkplätze?' }
  ];

  const bewertungsKennzahlen = [
    { kennzahl: 'Nettoanfangsrendite', zielwert: '4,5-6,5%', bedeutung: 'Höher = günstigerer Preis oder höhere Miete' },
    { kennzahl: 'WAULT', zielwert: '> 7 Jahre', bedeutung: 'Längere Mietbindung = höherer Preis (ca. +8% pro Jahr WAULT)' },
    { kennzahl: 'Verkaufspreis/qm VKF', zielwert: '€ 2.500-6.000', bedeutung: 'Je nach Lage, Mieter, Zustand. Discounter günstig, Bio-Supermarkt teuer.' },
    { kennzahl: 'Mietrendite zu Marktmiete', zielwert: '85-115%', bedeutung: 'Über 100% = überteuert (Risiko). Unter 85% = Upside-Potenzial.' }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO title="Phase 2: Bewertung & Positionierung – Verkaufsprozess Handelsimmobilie" description="Detaillierte Anleitung zu Phase 2: Professionelle Wertermittlung, Marktanalyse, Kennzahlen. Wie wird der Verkaufspreis festgelegt?" url="/ablauf/phase-2-bewertung" />
      <Navbar />
      <main className="flex-grow bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/ablauf" className="text-sm hover:underline mb-4 inline-block" style={{ color: 'var(--navy-600)' }}>← Zurück zur Übersicht</Link>
          
          <div className="flex items-center gap-4 mb-8">
            <div className="w-20 h-20 rounded-full flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #0F2744 0%, #2563A0 100%)' }}>
              <span className="text-4xl font-bold text-white">02</span>
            </div>
            <div>
              <div className="w-16 h-1 bg-gradient-to-r from-[#0F2744] to-[#2563A0] mb-2"></div>
              <h1 className="text-4xl font-bold" style={{ color: 'var(--navy-900)' }}>Bewertung & Positionierung</h1>
              <div className="flex items-center gap-2 mt-2">
                <Clock className="h-5 w-5" style={{ color: 'var(--gold-600)' }} />
                <p className="text-lg font-semibold" style={{ color: 'var(--gray-700)' }}>Dauer: 1-2 Wochen</p>
              </div>
            </div>
          </div>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>Warum ist diese Phase so wichtig?</h2>
            <p className="text-lg mb-4 leading-relaxed" style={{ color: 'var(--gray-700)' }}>
              Der Verkaufspreis entscheidet über Erfolg oder Misserfolg. <strong>Zu teuer = keine Anfragen, zu günstig = Geld verschenkt.</strong> Eine professionelle Bewertung berücksichtigt nicht nur Quadratmeter und Miete, sondern auch Standortqualität, Mieterrisiko, Vertragslaufzeiten und Marktlage.
            </p>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--gray-700)' }}>
              <strong>Investoren kaufen keine Immobilien – sie kaufen Cashflows und Renditechancen.</strong> In dieser Phase übersetzen wir Ihr Objekt in die Sprache institutioneller Käufer: Yield, WAULT, Capex-Bedarf, Exit-Szenarien.
            </p>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--navy-800)' }}>Bewertungs-Checkliste</h2>
            <div className="space-y-4">
              {bewertungsSchritte.map((s, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <CheckCircle2 className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: s.pflicht ? 'var(--gold-600)' : 'var(--navy-400)' }} />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-bold" style={{ color: 'var(--navy-800)' }}>{s.titel}</h3>
                      {s.pflicht && (
                        <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--gold-100)', color: 'var(--gold-700)' }}>
                          Pflicht
                        </span>
                      )}
                    </div>
                    <p className="text-sm" style={{ color: 'var(--gray-600)' }}>{s.details}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--navy-800)' }}>Wichtige Bewertungs-Kennzahlen</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2" style={{ borderColor: 'var(--gold-600)' }}>
                    <th className="text-left py-4 px-4 font-bold" style={{ color: 'var(--navy-800)' }}>Kennzahl</th>
                    <th className="text-left py-4 px-4 font-bold" style={{ color: 'var(--navy-800)' }}>Zielwert</th>
                    <th className="text-left py-4 px-4 font-bold" style={{ color: 'var(--navy-800)' }}>Bedeutung</th>
                  </tr>
                </thead>
                <tbody>
                  {bewertungsKennzahlen.map((k, i) => (
                    <tr key={i} className="border-b" style={{ borderColor: 'var(--gray-200)' }}>
                      <td className="py-4 px-4 font-semibold" style={{ color: 'var(--navy-700)' }}>{k.kennzahl}</td>
                      <td className="py-4 px-4 font-bold" style={{ color: 'var(--gold-600)' }}>{k.zielwert}</td>
                      <td className="py-4 px-4 text-sm" style={{ color: 'var(--gray-700)' }}>{k.bedeutung}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--navy-800)' }}>Verantwortlichkeiten</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {verantwortlichkeiten.map((v, i) => (
                <div key={i} className="p-6 rounded-lg" style={{ backgroundColor: i === 1 ? 'var(--gold-100)' : 'var(--navy-50)' }}>
                  <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>{v.wer}</h3>
                  <ul className="space-y-3">
                    {v.aufgaben.map((a, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                        <span className="font-bold" style={{ color: 'var(--gold-600)' }}>•</span>
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>Typische Probleme & Lösungen</h2>
            <div className="space-y-6">
              {typischeProbleme.map((item, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-red-50 rounded-lg">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-5 w-5" style={{ color: 'var(--error-600)' }} />
                      <h3 className="font-bold" style={{ color: 'var(--error-600)' }}>Problem:</h3>
                    </div>
                    <p className="text-sm" style={{ color: 'var(--gray-800)' }}>{item.problem}</p>
                  </div>
                  <div>
                    <h4 className="font-bold mb-2" style={{ color: 'var(--success-600)' }}>Lösung:</h4>
                    <p className="text-sm" style={{ color: 'var(--gray-700)' }}>{item.loesung}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>Expertentipp: Preis-Optimierung VOR Verkauf</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 bg-green-50 rounded-lg">
                <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--success-700)' }}>✓ Do's</h3>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                  <li>• Mietvertrag VOR Verkauf verlängern (+5 Jahre = +15-20% Wert)</li>
                  <li>• Kleine Reparaturen erledigen (Fassade, Parkplatz, Beleuchtung)</li>
                  <li>• Mieter-Bonität durch Creditreform dokumentieren lassen</li>
                  <li>• Indexierung im Vertrag nachweisen ("inflationssicher")</li>
                  <li>• Verkaufszeitpunkt wählen: Sept-Nov = beste Liquidität am Markt</li>
                </ul>
              </div>
              <div className="p-6 bg-red-50 rounded-lg">
                <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--error-700)' }}>✗ Don'ts</h3>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                  <li>• Großen Umbau starten (ROI meist negativ bei Verkauf)</li>
                  <li>• Preis am "Wunschdenken" festmachen statt an Marktdaten</li>
                  <li>• Schönrechnen (Käufer merken es in Due Diligence)</li>
                  <li>• Gutachter beauftragen ohne zu prüfen ob Käufer es braucht (€ 5k gespart)</li>
                  <li>• Im Dezember/Januar verkaufen (Investoren im Weihnachtsurlaub)</li>
                </ul>
              </div>
            </div>
          </Card>

          <div className="flex gap-4">
            <Link to="/ablauf/phase-1-unterlagen" className="flex-1">
              <Button variant="outline" className="w-full font-semibold py-6" style={{ borderColor: 'var(--navy-600)', color: 'var(--navy-600)' }}>
                ← Zurück zu Phase 1
              </Button>
            </Link>
            <Link to="/ablauf/phase-3-vermarktung" className="flex-1">
              <Button className="w-full btn-gold-gradient text-white border-0 font-semibold py-6">
                Weiter zu Phase 3: Vermarktung
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Phase2Bewertung;
